/**
 * A análise da licitação, nas doze seções do formato da Nectar.
 *
 * O formato veio pronto de fora — a análise do PE 010/2026 de Guaribas/PI,
 * feita à mão — com o pedido de que TODAS saíssem assim.
 *
 * ── uma estrutura só, com ou sem o edital lido ───────────────────────────
 *
 * A primeira versão tinha dois painéis: as doze seções quando o edital fora
 * lido, e uma caixa solitária de "ainda não foi lido" quando não. A Nectar
 * apontou o defeito, e ele é real — quem vê dois documentos do mesmo sistema
 * com estruturas diferentes não conclui "são gêneros distintos", conclui que o
 * sistema está quebrado.
 *
 * Agora as doze seções aparecem sempre, e as que dependem do edital DIZEM que
 * ele não foi lido. É a mesma regra do PDF, e a mesma que já valia dentro de
 * cada seção: vazio não some, porque sumir faz "não procuramos" virar
 * "procuramos e não achamos".
 *
 * ── a seção 5 muda de SENTIDO, e não só de conteúdo ──────────────────────
 *
 * Sem o edital, são CANDIDATOS que o cruzamento achou por semelhança de
 * palavras. Com ele, são os que a leitura confirmou e hierarquizou. Chamar os
 * dois de "atestados a submeter" seria a pior confusão possível deste painel:
 * a licitação de maior score desta base tinha afinidade 0,99 e é NO-GO.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ErroApi,
  type Analise,
  type LicitacaoDetalhe,
} from "../api/cliente";
import { descartadaPeloFunil } from "./Selos";
import { dataHora, dia } from "../formato";
import { Secao } from "./Secao";
import css from "./PainelAnalise.module.css";

const CLASSE_GO: Record<string, string | undefined> = {
  GO: css.go,
  AVALIAR: css.condicional,
  "NO-GO": css.nogo,
};

function reais(v: number | null | undefined) {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function pct(v: number | null | undefined) {
  if (v === null || v === undefined) return "—";
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

function numero(v: number | null | undefined) {
  return (v ?? 0).toLocaleString("pt-BR");
}

/** Vazio NÃO some: some faria "não achou" virar "não procurou". */
function Vazio({ children }: { children: React.ReactNode }) {
  return <p className={css.vazio}>{children}</p>;
}

/** Curto de propósito. O cabeçalho e a seção 12 explicam por extenso; aqui a
 *  frase precisa caber sem empurrar o conteúdo real para longe do título. */
function Falta() {
  return <Vazio>Sai da leitura do edital, que ainda não foi feita.</Vazio>;
}

/** O veredito em linguagem de decisão; a sigla vai ao lado, menor. */
/** A cor do veredito: o filete da seção e o nome. */
const TOM_VEREDITO: Record<string, "ok" | "atencao" | "perigo"> = {
  GO: "ok",
  AVALIAR: "atencao",
  "NO-GO": "perigo",
};
const CLASSE_COR: Record<string, string | undefined> = {
  GO: css.corOk,
  AVALIAR: css.corAtencao,
  "NO-GO": css.corPerigo,
};

const DECISAO: Record<string, string> = {
  GO: "Participar",
  AVALIAR: "Avaliar antes de decidir",
  "NO-GO": "Não participar",
};


const ORDEM_GRAU: Record<string, number> = { alto: 0, medio: 1, baixo: 2 };

/** "ALTO", "MÉDIO", "medio" → a classe do grau, sem acento. */
function grau(c: string | null | undefined): string {
  return (c ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function rotuloGrau(c: string | null | undefined): string {
  const g = grau(c);
  return g === "alto" ? "Alto" : g === "medio" ? "Médio" : g === "baixo" ? "Baixo" : (c ?? "—");
}

/** "Único", "1", "Lote 2" → "Lote único", "Lote 1", "Lote 2". */
function nomeDoLote(n: string | null | undefined): string {
  if (!n) return "Lote";
  const t = n.trim();
  return /^lote/i.test(t) ? t : `Lote ${t.charAt(0).toLowerCase()}${t.slice(1)}`;
}

/**
 * O TEXTO LONGO, RECOLHIDO EM N LINHAS, com "Ler tudo" — que só aparece quando
 * o texto passa delas (medido no próprio elemento, e de novo quando a largura
 * muda). Nada é cortado de verdade: está tudo ali, um clique adiante.
 */
function Recolhido({
  linhas,
  className = "",
  children,
}: {
  linhas: number;
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [aberto, setAberto] = useState(false);
  const [passa, setPassa] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || aberto) return;
    const mede = () => setPassa(el.scrollHeight > el.clientHeight + 1);
    mede();
    const obs = new ResizeObserver(mede);
    obs.observe(el);
    return () => obs.disconnect();
  }, [aberto, children]);
  return (
    <div className={className}>
      <div
        ref={ref}
        className={aberto ? undefined : css.recolhido}
        style={aberto ? undefined : ({ WebkitLineClamp: linhas, lineClamp: linhas } as React.CSSProperties)}
      >
        {children}
      </div>
      {passa || aberto ? (
        <button type="button" className={css.lerTudo} onClick={() => setAberto((v) => !v)}
                aria-expanded={aberto}>
          {aberto ? "Recolher" : "Ler tudo"}
        </button>
      ) : null}
    </div>
  );
}

/** Os primeiros `quantos` itens, e o resto atrás de "Ver mais N". */
function MostraAlguns<T>({
  itens,
  quantos,
  className,
  numerada,
  render,
}: {
  itens: T[];
  quantos: number;
  className?: string;
  /** Lista em que a ordem é dado (as perguntas a protocolar). */
  numerada?: boolean;
  render: (x: T) => React.ReactNode;
}) {
  const [todos, setTodos] = useState(false);
  const sobra = itens.length - quantos;
  const visiveis = todos || sobra <= 1 ? itens : itens.slice(0, quantos);
  const Lista = numerada ? "ol" : "ul";
  return (
    <>
      <Lista className={className}>
        {visiveis.map((x, i) => (
          <li key={i}>{render(x)}</li>
        ))}
      </Lista>
      {sobra > 1 ? (
        <button type="button" className={css.verMais} onClick={() => setTodos((v) => !v)}
                aria-expanded={todos}>
          {todos ? "Mostrar menos" : `Ver mais ${sobra}`}
        </button>
      ) : null}
    </>
  );
}

/** O que só aparece a um clique (a conta do score, a triagem). */
function Alterna({
  rotulo,
  aberta = false,
  children,
}: {
  rotulo: string;
  aberta?: boolean;
  children: React.ReactNode;
}) {
  const [aberto, setAberto] = useState(aberta);
  return (
    <>
      {aberto ? <div className={css.alternado}>{children}</div> : null}
      <button type="button" className={css.verMais} aria-expanded={aberto}
              onClick={() => setAberto((v) => !v)}>
        {aberto ? "Recolher" : rotulo}
      </button>
    </>
  );
}

const DOCUMENTO: [RegExp, string][] = [
  [/^edital/i, "Edital"],
  [/^(tr|termo)/i, "TR"],
  [/^minuta/i, "Minuta"],
  [/^projeto/i, "Projeto básico"],
];

/**
 * OS ITENS DO EDITAL QUE UM TEXTO CITA: "itens 5.1.4 do edital e 6.1.25 do
 * TR" → ["Edital 5.1.4", "TR 6.1.25"]. Conta como item o número com ponto
 * (5.1.4) que vem depois de "item", "subitem", "cláusula" ou "alínea", ou
 * antes de "do edital", "do TR", "da minuta", "do anexo X" — e nunca o
 * valor em reais (30.882.523,14) nem a lei (14.133/2021). Só lê o que a
 * análise já escreveu: o item que ela não citou não aparece.
 */
function itensCitados(t: string): string[] {
  const achados: string[] = [];
  // O fim do último item aceito e o documento dele: o número seguinte, ligado
  // por vírgula ou "e" ("itens 4.1 e 4.2"), é item do mesmo documento.
  let ultimo = -1;
  let ultimoNome = "Item";
  for (const m of t.matchAll(/\d+(?:\.\d+)+(?:\.[a-z]\b)?/g)) {
    const i = m.index ?? 0;
    const fim = i + m[0].length;
    if (/^[,/]\d/.test(t.slice(fim, fim + 2))) continue; // reais, lei
    if (/R\$\s*$/.test(t.slice(Math.max(0, i - 4), i))) continue;
    const depois = t.slice(fim, fim + 40);
    const antes = t.slice(Math.max(0, i - 30), i);
    const doc = depois.match(/^\s*(?:,\s*)?(?:do|da)\s+(edital|TR\b|termo de refer[êe]ncia|minuta|projeto b[áa]sico|anexo\s+[IVX\d]+)/i);
    const palavra = /(?:item|itens|subitem|subitens|cl[áa]usulas?|al[íi]neas?)\s+(?:n[º°o.]\s*)?$/i.test(antes);
    const lista = ultimo >= 0 && /^\s*(?:,|e)\s*$/.test(t.slice(ultimo, i));
    if (!doc && !palavra && !lista) continue;
    let nome = lista && !doc ? ultimoNome : "Item";
    if (doc) {
      const d = doc[1]!;
      nome = DOCUMENTO.find(([r]) => r.test(d))?.[1]
        ?? d.replace(/^anexo\s+/i, "Anexo ").replace(/\s+/g, " ");
    }
    ultimo = fim;
    ultimoNome = nome;
    const r = `${nome} ${m[0]}`;
    if (!achados.includes(r)) achados.push(r);
  }
  return achados;
}

/** Os itens citados, em fonte de dado, embaixo do texto que os cita. */
function Itens({ texto }: { texto: string }) {
  const itens = itensCitados(texto);
  if (!itens.length) return null;
  return (
    <span className={css.itensCitados} aria-label="Itens do edital citados">
      {itens.map((x) => <span key={x}>{x}</span>)}
    </span>
  );
}

/**
 * O numero e o que o sustenta, dentro da recomendacao final.
 *
 * A secao 11 dizia o veredito do edital e mandava procurar a conta do score
 * "acima nesta pagina" — o que faz quem esta decidindo rolar para tras no
 * momento em que ele mais precisa de tudo junto.
 *
 * Aqui vai o COMPACTO: a frase que explica de onde o numero veio e os
 * atestados que o cruzamento achou. A tabela dos cinco criterios continua so
 * em "Como o score foi calculado" — repeti-la seria o defeito que esta tela ja
 * tem com a sintese, impressa duas vezes sob dois titulos diferentes.
 *
 * 404 e resposta VALIDA: significa "nao pontuada". Ausencia de numero nao e
 * numero ruim, e o bloco diz isso em vez de sumir.
 */
function PorTrasDoNumero({ d }: { d: LicitacaoDetalhe }) {
  const { data: j, isPending } = useQuery({
    queryKey: ["score", d.id],
    queryFn: () => api.scoreDe(d.id),
    retry: false,
  });

  if (isPending) return null;
  if (!j) {
    return (
      <p className={css.corrido}>
        Esta licitação não tem score nesta versão do pipeline — não é score
        zero, é ausência dele.
      </p>
    );
  }

  return (
    <>
      <p className={css.corrido}>
        <strong>Score {j.valor ?? "—"}</strong> de 100, apurado sobre{" "}
        {j.peso_total} pontos de peso (limiar {j.limiar}). {j.resumo}
      </p>
      {j.atestados.length > 0 ? (
        <p className={css.corrido}>
          <strong>Atestados cruzados:</strong>{" "}
          {j.atestados
            .map(
              (a) =>
                `${a.codigo} ${a.entidade}` +
                // O projeto que a gestão informou cruza, e não é atestado (D14).
                (a.natureza === "projeto" ? " — projeto, sem atestado no sistema" : "") +
                ` (${a.semelhanca.toFixed(3)})`,
            )
            .join(" · ")}
        </p>
      ) : (
        <p className={css.corrido}>
          <strong>Nenhum atestado</strong> do acervo passou do piso do
          cruzamento — o casador não chegou a oferecer candidatos, o que não é o
          mesmo que ter oferecido e nenhum servir.
        </p>
      )}
    </>
  );
}

/**
 * AS DOZE SEÇÕES, EM TRÊS ABAS (19/10): a análise deixou de ser uma página só.
 *
 *   · `parecer`  — "Visão geral": o veredito, a conclusão, os pontos favoráveis
 *                  e críticos e a recomendação final;
 *   · `ficha`    — "Edital e requisitos", em cima: a ficha do edital;
 *   · `edital`   — "Edital e requisitos", embaixo: os grupos, a documentação
 *                  exigida, os custos e o plano;
 *   · `evidencias` — "Documentos e evidências": os trechos citados;
 *   · `rastreio` — "Documentos e evidências": as fontes e a metodologia.
 *
 * O objeto e os atestados (as seções 3 e 5) saíram daqui: o objeto abre a
 * Visão geral, e os atestados têm a aba deles (`AtestadosDaOportunidade`).
 * Nada da lógica mudou, e as consultas são as mesmas (mesmas chaves de cache).
 */
export function PainelAnalise({
  d,
  parte,
}: {
  d: LicitacaoDetalhe;
  parte: "parecer" | "ficha" | "edital" | "evidencias" | "rastreio";
}) {
  const qa = useQuery({
    queryKey: ["analise", d.id],
    queryFn: () => api.analiseDe(d.id),
    retry: false,
  });
  // A LEITURA DO EDITAL É A ÚNICA AÇÃO CARA DESTA TELA: ~US$ 1,40 por clique,
  // contra US$ 0,0088 da triagem. Por isso ela só aparece depois da
  // pré-aprovação — é o fluxo pedido pela Nectar (o sistema sugere, a pessoa
  // aprova, e só então a IA lê) e o que a migração 0044 passou a exigir na
  // fila. A guarda de verdade é do servidor, que responde 422; aqui o botão
  // apenas não convida para um clique que seria recusado.
  const cliente = useQueryClient();
  const ler = useMutation({
    mutationFn: () => api.pedirAnalise(d.id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["analise", d.id] });
    },
  });

  // A análise roda num subprocesso e leva minutos: o 202 só diz que começou.
  // Este relógio repergunta de dez em dez segundos e para quando a análise
  // aparece — ou quando desiste.
  //
  // O TETO NÃO É DETALHE. O subprocesso sai com DEVNULL, então quando ele
  // morre — e ele morre: a licitação 1950 tem treze documentos e NENHUM com
  // texto, todos digitalizados, um deles nem é PDF — nada avisa esta tela.
  // Sem o teto, "o edital não pôde ser lido" ficaria indistinguível de "ainda
  // está lendo", para sempre. É a mesma regra que o resto do sistema já segue
  // em `parcial` e nos três estados do edital: não colapsar dois estados
  // diferentes num só, e nunca deixar a falha parecer progresso.
  const DESISTE_APOS = 60; // 10 minutos; as leituras medidas levam 3 a 4
  const [esperas, setEsperas] = useState(0);
  useQuery({
    queryKey: ["analise-espera", d.id],
    queryFn: async () => {
      setEsperas((n) => n + 1);
      await cliente.invalidateQueries({ queryKey: ["analise", d.id] });
      return Date.now();
    },
    enabled: ler.isSuccess && !qa.data && esperas < DESISTE_APOS,
    refetchInterval: 10_000,
  });
  const desistiu = ler.isSuccess && !qa.data && esperas >= DESISTE_APOS;

  if (qa.isPending) return <p className={css.espera}>Carregando a análise…</p>;

  const quebrou = qa.error && !(qa.error instanceof ErroApi && qa.error.status === 404);
  if (quebrou) {
    return (
      <section className={css.painel}>
        <h2 className={css.cabeca}>Análise</h2>
        <div className={css.ausente}>
          <strong>Não deu para carregar a análise.</strong>
          <p>{qa.error instanceof Error ? qa.error.message : "erro desconhecido"}</p>
        </div>
      </section>
    );
  }

  const a: Analise | null = qa.data ?? null;
  const lido = a !== null;
  const c = a?.certame;
  const cob = a?.cobertura;
  const lidoPct =
    cob && cob.publicados ? Math.round((cob.lidos / cob.publicados) * 100) : null;
  // A DESCARTADA PELO FUNIL (vencida, suspensa, revogada, anulada): o
  // `descartar` dela e do prazo, nao do score, entao nao ha divergencia a
  // mostrar, e o botao pago nao aparece — ela nao passa por IA (2026-09-22).
  const descartada = descartadaPeloFunil(d.motivo_aba);
  // Os riscos do mais grave para o menos: o alto é o que decide.
  const criticos = [...(a?.criticos ?? [])].sort(
    (x, y) => (ORDEM_GRAU[grau(x.criticidade)] ?? 3) - (ORDEM_GRAU[grau(y.criticidade)] ?? 3),
  );
  const altos = criticos.filter((r) => grau(r.criticidade) === "alto").length;


  return (
    <section className={css.painel}>
      {parte === "parecer" && (
        <>
      {/* ── O PARECER COMO UM PARECER (28/10) ───────────────────────────────
          "Ainda tá tudo bem genérico e nada profissional, quero uma sensação
          de originalidade." A forma vem do documento que ele substitui: o
          parecer técnico, com as RUBRICAS NA MARGEM — Veredito, Fundamento,
          Resumo, Riscos… — e o texto ao lado, separados por filetes, sem
          caixa. Cada risco traz, em fonte de dado, os ITENS DO EDITAL que ele
          cita (`itensCitados`), e a cobertura da leitura é uma régua: quanto
          do edital foi lido é o que diz quanto o veredito pesa. A ordem é a da
          decisão; nada do conteúdo saiu, e nada aparece duas vezes. */}

      <Secao titulo="Veredito" sub="da pré-análise"
             tom={lido ? (TOM_VEREDITO[a.recomendacao ?? ""] ?? "nao") : "nao"}>
        {lido ? (
          <>
            <div className={css.vereditoLinha}>
              <strong className={`${css.vereditoNome} ${CLASSE_COR[a.recomendacao ?? ""] ?? ""}`}>
                {DECISAO[a.recomendacao ?? ""] ?? "Sem veredito"}
              </strong>
            </div>
            <p className={css.procedencia}>
              {[
                a.criado_em ? `Lida em ${dia(a.criado_em)}` : null,
                cob?.leitura_id ? "sobre a leitura integral certificada" : null,
                // Quanto do edital foi lido, dito em texto (28/10: a régua saiu).
                lidoPct === null ? null : lidoPct >= 100 ? "edital lido inteiro" : `${lidoPct}% do edital lido`,
                cob?.documentos ? `${cob.documentos} ${cob.documentos === 1 ? "documento" : "documentos"}` : null,
              ].filter(Boolean).join(" · ")}
            </p>
            {/* QUEM DECIDE, DITO EM UMA LINHA: a hierarquia — o edital ganha
                do parecer, que ganha do score — mora no backend. */}
            <p className={css.regra}>
              Prevalece sobre o score: este veredito leu o edital, e o score mede
              só a frase do objeto contra o acervo.
            </p>
          </>
        ) : (
          <>
            <strong className={`${css.vereditoNome} ${css.vereditoNaoLido}`}>Edital ainda não lido</strong>
            <Recolhido linhas={2} className={css.prosaParecer}>
              Os pontos que dependem do edital estão vazios e dizem isso: o que
              existe aqui vem do <strong>objeto</strong> — uma ou duas frases — e
              do cruzamento com o acervo. A diferença já foi medida: a licitação
              de maior score desta base tirou 84 sobre 100 pontos de critério, e
              a leitura do edital a classificou <strong>NO-GO</strong>; os 84
              saíram de uma frase em que “engenharia”, “CREA” e “BIM” não
              aparecem.
            </Recolhido>
            {/* O BOTAO NAO EXIGE APROVACAO (0061): a leitura roda para todas,
                e quem pode pedir e quem opera — a API recusa os outros. */}
            {descartada ? (
              <p className={css.prosaParecer}>
                <strong>{d.motivo_aba?.texto}.</strong> {d.motivo_aba?.ajuda}
              </p>
            ) : (
            <div className={css.disparo}>
              <button
                type="button"
                onClick={() => ler.mutate()}
                disabled={ler.isPending || (ler.isSuccess && !desistiu)}
              >
                {ler.isPending
                  ? "Enviando…"
                  : desistiu
                    ? "Tentar de novo"
                    : ler.isSuccess
                      ? "Lendo o edital…"
                      : "Ler o edital agora"}
              </button>
              <small>
                {desistiu
                  ? null
                  : ler.isSuccess
                    ? "A leitura leva alguns minutos e roda em segundo plano. Esta página se atualiza sozinha quando terminar."
                    : "A leitura vai até 180 mil caracteres do edital e leva alguns minutos."}
              </small>
              {desistiu && (
                <small className={css.erroDisparo}>
                  Dez minutos sem resultado. O mais provável é que nenhum
                  documento desta licitação tenha texto extraível — PDF
                  digitalizado, ou arquivo que não é PDF. Rode{" "}
                  <code>uv run nectuno-analise {d.id} --seco</code> no
                  terminal para ver o motivo documento por documento; o modo
                  seco não custa nada.
                </small>
              )}
              {ler.error && (
                <small className={css.erroDisparo}>
                  {ler.error instanceof Error
                    ? ler.error.message
                    : "não deu para pedir a leitura"}
                </small>
              )}
            </div>
            )}
            <p className={css.prosaParecer}>
              {descartada
                ? "Ninguém leu o edital desta licitação, e ninguém vai ler."
                : <>Sem o edital lido, a única recomendação é a do{" "}
                    <strong>score</strong> — “{d.recomendacao ?? "sem análise"}” —,
                    calculada sobre o objeto. O score <strong>ordena a fila; ele
                    não decide.</strong></>}
            </p>
          </>
        )}
      </Secao>

      {lido && !a.evidencia_ok && (
        <p className={css.alerta}>
          <strong>Revisão humana obrigatória.</strong> Pelo menos um trecho
          citado nesta análise não foi encontrado literalmente no texto do
          edital. Trate cada afirmação como não verificada até conferir na
          fonte.
        </p>
      )}

      {/* O FUNDAMENTO: a razão que decide, tirada da própria análise. No NO-GO
          é o risco mais grave; no GO, o primeiro ponto a favor; no AVALIAR,
          os dois lados. */}
      {lido && (criticos[0] || a.favoraveis[0]) ? (
        <Secao titulo="Fundamento">
          <dl className={css.fundamento}>
            {criticos[0] && a.recomendacao !== "GO" ? (
              <div>
                <dt className={css.fundContra}>
                  {a.recomendacao === "NO-GO" ? "Impedimento" : "Pesa contra"}
                </dt>
                <dd>
                  {criticos[0].ponto}
                  <Itens texto={`${criticos[0].ponto} ${criticos[0].impacto ?? ""}`} />
                </dd>
              </div>
            ) : null}
            {a.favoraveis[0] && a.recomendacao !== "NO-GO" ? (
              <div>
                <dt className={css.fundFavor}>Pesa a favor</dt>
                <dd>{a.favoraveis[0]}</dd>
              </div>
            ) : null}
          </dl>
        </Secao>
      ) : null}

      <Secao titulo="Resumo">
        {lido ? (
          <Recolhido linhas={4} className={css.prosaParecer}>{a.sintese}</Recolhido>
        ) : (
          <Falta />
        )}
      </Secao>

      <Secao titulo="Riscos" conta={lido ? a.criticos.length : undefined}
               sub={lido && altos ? `${altos} ${altos === 1 ? "alto" : "altos"}` : undefined}
               id={`riscos-${d.id}`}>
        {!lido ? (
          <Falta />
        ) : a.criticos.length === 0 ? (
          <Vazio>
            Nenhum risco identificado — o que é raro, e vale conferir quanto do
            edital foi lido, em Documentos e evidências.
          </Vazio>
        ) : (
          <MostraAlguns
            itens={criticos}
            quantos={Math.max(3, altos)}
            className={css.riscos2}
            render={(r) => (
              <div className={`${css.risco2} ${css[`grau_${grau(r.criticidade)}`] ?? ""}`}>
                <span className={css.grauNome}>{rotuloGrau(r.criticidade)}</span>
                <strong>{r.ponto}</strong>
                {r.impacto ? (
                  <Recolhido linhas={2} className={css.impacto}>{r.impacto}</Recolhido>
                ) : null}
                <Itens texto={`${r.ponto} ${r.impacto ?? ""}`} />
              </div>
            )}
          />
        )}
      </Secao>

      <Secao titulo="A favor" conta={lido ? a.favoraveis.length : undefined}>
        {!lido ? (
          <Falta />
        ) : a.favoraveis.length === 0 ? (
          <Vazio>Nenhum ponto favorável foi identificado na leitura.</Vazio>
        ) : (
          <MostraAlguns itens={a.favoraveis} quantos={4} className={css.favor2}
                       render={(f) => f} />
        )}
      </Secao>

      <Secao titulo="Por lote" conta={lido && a.grupos.length > 1 ? a.grupos.length : undefined}>
        {!lido ? (
          <Falta />
        ) : a.grupos.length === 0 ? (
          <Vazio>O edital não foi lido por grupos — ver Grupos e valor previsto, na aba Edital e requisitos.</Vazio>
        ) : (
          <ul className={css.lotes}>
            {a.grupos.map((g, i) => (
              <li key={g.numero ?? i}>
                <div className={css.loteCabeca}>
                  <b className={css.loteNome}>{nomeDoLote(g.numero)}</b>
                  <span className={`${css.loteVeredito} ${CLASSE_GO[g.recomendacao ?? ""] ?? ""}`}>
                    {DECISAO[g.recomendacao ?? ""] ?? g.recomendacao ?? "—"}
                  </span>
                  <span className={css.loteValor}>{reais(g.teto)}</span>
                </div>
                <Recolhido linhas={2} className={css.loteTexto}>{g.descricao}</Recolhido>
                {g.porque ? (
                  <Recolhido linhas={2} className={css.lotePorque}>{g.porque}</Recolhido>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Secao>

      {lido && a.esclarecimentos.length > 0 ? (
        <Secao titulo="Perguntas ao órgão" conta={a.esclarecimentos.length}
                 sub="a protocolar">
          <MostraAlguns itens={a.esclarecimentos} quantos={3} className={css.perguntas}
                        numerada render={(e) => e} />
        </Secao>
      ) : null}

      {/* O PORQUÊ DO NÚMERO, a um clique: a conta inteira está em "Como o
          score foi calculado", na aba Atestados. */}
      <Secao titulo="Score">
        <Alterna rotulo="Ver de onde vem o score">
          <PorTrasDoNumero d={d} />
        </Alterna>
      </Secao>

      {/* A TRIAGEM DO OBJETO, a primeira leitura: aberta quando o edital
          ainda não foi lido (aí ela é tudo o que há). */}
      {d.justificativa || d.riscos.length > 0 ? (
        <Secao titulo="Triagem do objeto"
                 sub={d.riscos.length ? `${d.riscos.length} a conferir` : undefined}>
          <Alterna rotulo="Ver o que a triagem disse" aberta={!lido}>
            {d.justificativa ? <p className={css.prosaParecer}>{d.justificativa}</p> : null}
            {d.riscos.length > 0 ? (
              <MostraAlguns itens={d.riscos} quantos={3} className={css.favor2} render={(x) => x} />
            ) : null}
          </Alterna>
        </Secao>
      ) : null}
        </>
      )}

      {parte === "ficha" && (
        <>
      <dl className={css.ficha}>
        {(
          [
            ["Órgão", c?.orgao || d.orgao],
            ["Município / UF", [d.municipio, d.uf].filter(Boolean).join(" / ")],
            ["Número do edital", c?.numero || d.numero],
            ["Processo", c?.processo || d.processo],
            ["Modalidade", c?.modalidade || d.modalidade],
            ["Critério de julgamento", c?.criterio_julgamento || d.criterio_julgamento],
            ["Modo de disputa", c?.modo_disputa || d.modo_disputa],
            ["Plataforma", c?.plataforma || d.sistema],
            ["Propostas até", c?.propostas_ate || dataHora(d.encerramento)],
            ["Abertura", c?.abertura || dataHora(d.abertura)],
            ["Início da disputa", c?.inicio_disputa],
            ["Esclarecimentos até", c?.esclarecimentos_ate],
            ["Vigência", c?.vigencia],
            ["Garantia", c?.garantia],
            ["Subcontratação", c?.subcontratacao],
            ["Valor máximo", lido ? reais(c?.valor_maximo) : d.valor_estimado],
            // Os campos que a ficha antiga do painel tinha e o quadro do edital
            // não: nenhum se perde ao juntar as duas.
            ["Prazo da proposta", d.prazo_proposta],
            ["Lei", d.lei],
            ["Fonte", d.fonte],
            ["Referência interna da oportunidade", d.codigo],
          ] as const
        ).map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt>{rotulo}</dt>
            <dd className={valor && valor !== "—" ? undefined : css.naoInformado}>{valor && valor !== "—" ? valor : "Não informado"}</dd>
          </div>
        ))}
      </dl>
      {!lido && (
        <p className={css.nota}>
          Os campos preenchidos acima vêm do registro da fonte, não do edital.
          Os vazios só existem dentro dele.
        </p>
      )}
        </>
      )}

      {parte === "edital" && (
        <>
      <Secao titulo="Grupos e valor previsto">
      {!lido ? (
        <Falta />
      ) : a.grupos.length === 0 ? (
        <Vazio>Lote único ou orçamento sigiloso.</Vazio>
      ) : (
        <div className={css.rolagem}>
          <table className={css.tabela}>
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Descrição</th>
                <th>Teto</th>
                <th>Análise</th>
              </tr>
            </thead>
            <tbody>
              {a.grupos.map((g, i) => (
                <tr key={g.numero ?? i}>
                  <td>{g.numero}</td>
                  <td>{g.descricao}</td>
                  <td>{reais(g.teto)}</td>
                  <td>{g.recomendacao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </Secao>

      <Secao titulo="Documentação exigida">
      {!lido ? (
        <Falta />
      ) : a.documentacao.length === 0 ? (
        <Vazio>A análise não extraiu a lista de habilitação.</Vazio>
      ) : (
        <>
          <ul className={css.documentos}>
            {a.documentacao.map((x, i) => (
              <li key={i}>
                <span
                  className={`${css.situacao} ${
                    x.situacao === "NO ACERVO" ? css.temos : css.naoindexado
                  }`}
                >
                  {x.situacao}
                </span>
                <div>
                  <strong>{x.documento}</strong>
                  <span>{x.exigencia}</span>
                </div>
              </li>
            ))}
          </ul>
          <p className={css.nota}>
            <strong>NÃO INDEXADO</strong> quer dizer que este sistema não guarda
            esse tipo de documento — ele indexa atestados, e nada de certidão,
            balanço, alvará ou contrato social. Não quer dizer que o Nectar não
            os tenha. Inventar uma ausência faria desistir de um certame por
            falta de um papel que está na gaveta da administração.
          </p>
        </>
      )}

      </Secao>

      <Secao titulo="Custos mínimos — modelo interno">
      {!lido ? (
        <Falta />
      ) : (
        <>
          <p className={css.premissas}>
            Instrutoria {reais(a.custos.premissas?.instrutoria_hora)}/h ·{" "}
            {a.custos.premissas?.horas_por_diaria} h por diária · administração{" "}
            {pct(a.custos.premissas?.administracao_pct)} · tributos{" "}
            {pct(a.custos.premissas?.tributos_pct)}. Editáveis em Configuração.
          </p>
          {a.custos.grupos.length === 0 ? (
            <Vazio>Nenhum grupo foi modelado.</Vazio>
          ) : (
            <div className={css.rolagem}>
              <table className={css.tabela}>
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Teto</th>
                    <th>Custo modelado</th>
                    <th>Custo/teto</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {a.custos.grupos.map((g, i) => (
                    <tr key={g.numero ?? i}>
                      <td>{g.numero}</td>
                      <td>{reais(g.teto)}</td>
                      {/* Nulo é "não modelado" e NÃO custo zero. Um R$ 0,00 aqui
                          diria que sobra a margem inteira sobre exatamente o
                          grupo que não se deve disputar. */}
                      <td className={g.custo === null ? css.naomodelado : ""}>
                        {g.custo === null ? "não modelado" : reais(g.custo)}
                      </td>
                      <td>{pct(g.custo_sobre_teto)}</td>
                      <td>{g.recomendacao}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={css.nota}>
            Estes números não são preço de proposta nem piso de lance: dependem
            de cotação real, contratação local e regime tributário.
          </p>
        </>
      )}

      </Secao>

      <Secao titulo="Plano até a sessão">
      {!lido ? (
        <Falta />
      ) : a.plano.length === 0 ? (
        <Vazio>Sem plano — a recomendação é não disputar.</Vazio>
      ) : (
        <ol className={css.plano}>
          {a.plano.map((p, i) => (
            <li key={i}>
              <b>{p.prazo}</b>
              <span>{p.acao}</span>
            </li>
          ))}
        </ol>
      )}

      </Secao>
        </>
      )}

      {parte === "evidencias" && (
        <>
          {!lido ? (
            <Vazio>Sem pré-análise do edital, não há trechos citados.</Vazio>
          ) : a.evidencias.length === 0 ? (
            <Vazio>A análise não citou trechos do edital.</Vazio>
          ) : (
            <>
              <p className={css.nota}>
                {a.evidencias.length} trecho(s) citado(s) —{" "}
                {a.evidencia_ok
                  ? "todos conferidos por código como existentes no edital."
                  : "COM FALHA na conferência: trate cada um como não verificado até conferir na fonte."}
              </p>
              <ul className={css.trechos}>
                {a.evidencias.map((e, i) => (
                  <li key={i}>
                    <span className={css.trechoFonte}>{e.campo ?? "Trecho citado"}</span>
                    <blockquote>{e.trecho}</blockquote>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {parte === "rastreio" && (
        <>
      {lido ? (
        <>
          <p className={css.corrido}>
            {cob?.documentos} documento(s) do PNCP · {numero(cob?.lidos)} de{" "}
            {numero(cob?.publicados)} caracteres publicados
            {lidoPct !== null && ` (${lidoPct}%)`} · {a.modelo}, esforço{" "}
            {a.effort} · {a.evidencias.length} trechos citados,{" "}
            {a.evidencia_ok
              ? "todos conferidos como existentes no edital"
              : "COM FALHA na conferência"}
            .
          </p>
          {/* A DECISÃO SOBRE A LEITURA (a2, Fase 4C): de onde ela partiu, e o
              que o código ajustou contra a matriz. */}
          {cob?.leitura_id && (
            <p className={css.corrido}>
              Decidida sobre a leitura integral certificada #{cob.leitura_id} —{" "}
              {cob.modo_leitura === "inteiro"
                ? "o edital inteiro"
                : "os fatos do edital inteiro (ele não cabia na janela)"}
              , com a matriz de {cob.exigencias ?? 0} exigências contra os atestados
              {(cob.matriz_ajustes ?? []).length > 0 &&
                `. Ajustado pelo código contra a matriz: ${(cob.matriz_ajustes ?? []).join("; ")}`}
              .
            </p>
          )}
          {cob && cob.cortados.length > 0 && (
            <div className={css.cortado}>
              <strong>O que esta análise não leu.</strong> Os documentos abaixo
              não couberam inteiros no limite de leitura. Silêncio desta análise
              sobre uma exigência pode significar que ela está no trecho não
              lido — não que o edital não a faça.
              <ul>
                {cob.cortados.map((x, i) => (
                  <li key={i}>
                    {x.titulo} — {numero(x.lidos)} de {numero(x.publicados)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cob && cob.ilegiveis.length > 0 && (
            <div className={css.cortado}>
              <strong>Documentos que não viraram texto.</strong> Foram
              publicados, mas o sistema não conseguiu lê-los, e o conteúdo deles
              não entrou nesta análise. Silêncio sobre o que eles dizem não é
              ausência.
              <ul>
                {cob.ilegiveis.map((x, i) => (
                  <li key={i}>
                    {x.titulo} — {x.motivo}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* O QUE FALTOU DENTRO DE CADA DOCUMENTO (extrator d3, 30/09): o
              documento virou texto, mas não inteiro — páginas escaneadas
              ainda sem OCR, membro de ZIP com senha, link do aviso que não
              abriu. Antes isto era só uma nota no fim do texto. */}
          {cob && (cob.lacunas ?? []).length > 0 && (
            <div className={css.cortado}>
              <strong>Documentos lidos com lacunas.</strong> Viraram texto, mas
              não inteiros — o que está listado abaixo não entrou nesta análise.
              <ul>
                {(cob.lacunas ?? []).map((x, i) => (
                  <li key={i}>
                    {x.titulo} —{" "}
                    {x.lacunas.map((l) => l.detalhe || l.codigo).join("; ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className={css.cortado}>
          <strong>Nenhum documento do certame foi lido.</strong> Tudo acima que
          depende do edital está vazio e assinalado. O silêncio deste painel
          sobre uma exigência de habilitação, um prazo ou um risco não significa
          que eles não existam — significa que ninguém olhou.
          <p>
            <code>uv run nectuno-analise {d.id}</code> lê o edital e preenche a
            análise. Custa cerca de US$ 1,50 e leva alguns minutos.
          </p>
        </div>
      )}
        </>
      )}
    </section>
  );
}
